import React, { useState } from 'react';
import { addDoc, collection, doc, setDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAuth } from '../../hooks/AuthContext';
import { ArrowLeft, TestTube, CheckCircle, XCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export const FirebaseTest: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [testResults, setTestResults] = useState<Array<{
    test: string;
    status: 'success' | 'error' | 'pending';
    message: string;
  }>>([]);
  const [testing, setTesting] = useState(false);

  const runTests = async () => {
    if (!user) {
      alert('Please log in first');
      return;
    }

    setTesting(true);
    setTestResults([]);

    const tests = [
      {
        name: 'User Authentication',
        test: async () => {
          if (!user) throw new Error('No user logged in');
          return `User ${user.uid} is authenticated`;
        }
      },
      {
        name: 'Test Journal Write',
        test: async () => {
          const testJournal = {
            content: 'Test journal entry',
            mood: 5,
            tags: ['test'],
            date: new Date().toISOString().split('T')[0],
            sharedScope: 'private',
            uid: user.uid,
            createdAt: new Date(),
            updatedAt: new Date()
          };
          
          const docRef = await addDoc(collection(db, 'users', user.uid, 'journals'), testJournal);
          return `Journal created with ID: ${docRef.id}`;
        }
      },
      {
        name: 'Test Group Creation',
        test: async () => {
          const testGroup = {
            name: 'Test Group - Please Delete',
            description: 'This is a test group for debugging',
            privacy: 'public',
            category: 'General Support',
            leaderId: user.uid,
            moderators: [user.uid],
            memberCount: 1,
            postCount: 0,
            lastActivity: new Date(),
            rules: ['Test rule'],
            createdAt: new Date(),
            updatedAt: new Date()
          };
          
          const groupRef = await addDoc(collection(db, 'groups'), testGroup);
          return `Group created with ID: ${groupRef.id}`;
        }
      },
      {
        name: 'Test Membership Creation',
        test: async () => {
          const membershipData = {
            groupId: 'test-group-id',
            userId: user.uid,
            role: 'member',
            joinedAt: new Date(),
            lastSeen: new Date(),
            postCount: 0,
            helpfulCount: 0,
            notifications: true,
            canPost: true,
            canInvite: false,
            warnings: 0
          };
          
          await setDoc(doc(db, 'groupMemberships', `${user.uid}_test-group-id`), membershipData);
          return 'Membership test successful';
        }
      }
    ];

    for (const test of tests) {
      try {
        setTestResults(prev => [...prev, { test: test.name, status: 'pending', message: 'Running...' }]);
        
        const result = await test.test();
        
        setTestResults(prev => prev.map(t => 
          t.test === test.name 
            ? { ...t, status: 'success', message: result }
            : t
        ));
      } catch (error: any) {
        console.error(`Test ${test.name} failed:`, error);
        
        setTestResults(prev => prev.map(t => 
          t.test === test.name 
            ? { ...t, status: 'error', message: error.message || 'Unknown error' }
            : t
        ));
      }
    }

    setTesting(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 p-4">
      <div className="max-w-4xl mx-auto space-y-8">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/community')}
            className="p-3 text-gray-400 hover:text-gray-600 rounded-xl hover:bg-white transition-all duration-200"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Firebase Connection Test</h1>
            <p className="text-gray-600 mt-1">Debug Firebase permissions and connectivity</p>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900">Run Firebase Tests</h2>
            <button
              onClick={runTests}
              disabled={testing || !user}
              className="flex items-center space-x-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              <TestTube className="w-5 h-5" />
              <span>{testing ? 'Testing...' : 'Run Tests'}</span>
            </button>
          </div>

          {user ? (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <p className="text-green-800">
                <strong>User:</strong> {user.displayName || 'Unknown'} ({user.uid})
              </p>
              <p className="text-green-800">
                <strong>Role:</strong> {user.role || 'user'}
              </p>
              <p className="text-green-800">
                <strong>Email:</strong> {user.email || 'Unknown'}
              </p>
            </div>
          ) : (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <p className="text-red-800">No user logged in. Please log in first.</p>
            </div>
          )}

          <div className="space-y-4">
            {testResults.map((result, index) => (
              <div key={index} className="flex items-start space-x-4 p-4 border border-gray-200 rounded-lg">
                <div className="flex-shrink-0 mt-1">
                  {result.status === 'success' && <CheckCircle className="w-5 h-5 text-green-500" />}
                  {result.status === 'error' && <XCircle className="w-5 h-5 text-red-500" />}
                  {result.status === 'pending' && (
                    <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                  )}
                </div>
                <div className="flex-1">
                  <h3 className="font-medium text-gray-900">{result.test}</h3>
                  <p className={`text-sm mt-1 ${
                    result.status === 'success' ? 'text-green-700' :
                    result.status === 'error' ? 'text-red-700' :
                    'text-gray-600'
                  }`}>
                    {result.message}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {testResults.length === 0 && !testing && (
            <div className="text-center py-8 text-gray-500">
              <TestTube className="w-12 h-12 mx-auto mb-4 text-gray-400" />
              <p>Click "Run Tests" to check Firebase connectivity and permissions</p>
            </div>
          )}
        </div>

        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6">
          <h3 className="font-medium text-yellow-900 mb-2">Debugging Tips</h3>
          <div className="text-yellow-800 text-sm space-y-1">
            <p>• Check browser console for detailed error messages</p>
            <p>• Verify you're logged in with a valid user account</p>
            <p>• Ensure Firestore security rules allow group creation</p>
            <p>• Check if there are any network connectivity issues</p>
          </div>
        </div>
      </div>
    </div>
  );
};
